import { useIntl } from 'react-intl';
import { useCallback, useMemo } from 'react';
import { ObservableQuery } from '@apollo/client';

import { useSnackbar } from '@ocx-app/lib/snackbar/snackbar';
import { useShowGenericErrorSnackbar } from '@ocx-app/hooks/useShowGenericErrorSnackbar';

import { PointsStrategy, useReturnPurchasedVoucherMutation } from '@ocx/graphql';
import { useConfirmationActionSheet } from '@ocx/feat-modals';
import { messages } from '@ocx/ui-offers';
import { useConfiguration } from '@ocx/data-configuration';

// Returning reward leads to disappearance of the offer from the list,
// so we can use hook params to prevent refetching the offer in order
// to avoid displaying "No offer found" UI (Offer details as example)
const OFFERS_QUERY_NAME = 'offers' as const;
const createRefetchOfferPredicate =
  (offerId: string) =>
  (observableQuery: ObservableQuery<unknown>): boolean =>
    observableQuery.queryName !== OFFERS_QUERY_NAME || observableQuery.variables?.idEquals !== offerId;

type UseReturnReward = (params: {
  onCompleted?: () => void;
  preventRefetchOffer?: { enabled: true; offerId: string };
}) => {
  returnReward: (offerId: string) => void;
  returningReward: boolean;
};

export const useReturnReward: UseReturnReward = ({ onCompleted, preventRefetchOffer }) => {
  const intl = useIntl();
  const snackbar = useSnackbar();
  const showGenericErrorSnackbar = useShowGenericErrorSnackbar();
  const { open: openConfirmationActionSheet } = useConfirmationActionSheet();

  const { pointsStrategy } = useConfiguration('loyaltyProgram');

  const [returnReward, { loading: isReturningReward }] = useReturnPurchasedVoucherMutation({
    refetchQueries: ['currentCustomer', 'offers', 'totalVouchersCount'],
    awaitRefetchQueries: true,
    onQueryUpdated: preventRefetchOffer?.enabled ? createRefetchOfferPredicate(preventRefetchOffer.offerId) : undefined,
  });

  const onSuccessfulReturn = useCallback(() => {
    snackbar.open(
      intl.formatMessage(
        pointsStrategy === PointsStrategy.Points
          ? messages.returnRewardForPointsSuccessText
          : messages.returnRewardForCentsSuccessText,
      ),
      { variant: 'success' },
    );
    onCompleted?.();
  }, [intl, onCompleted, pointsStrategy, snackbar]);

  const onReturnConfirmed = useCallback(
    async (offerId: string) => {
      return returnReward({
        variables: {
          input: { offerId },
        },
        onCompleted: onSuccessfulReturn,
        onError: showGenericErrorSnackbar,
      });
    },
    [onSuccessfulReturn, returnReward, showGenericErrorSnackbar],
  );

  const openReturnRewardConfirmation = useCallback(
    (offerId: string) => {
      openConfirmationActionSheet({
        title: intl.formatMessage(messages.returnRewardConfirmationTitle),
        description: intl.formatMessage(messages.returnRewardConfirmationText),
        confirmButtonText: intl.formatMessage(messages.returnRewardConfirmationButtonTitle),
        onConfirm: () => onReturnConfirmed(offerId),
      });
    },
    [intl, onReturnConfirmed, openConfirmationActionSheet],
  );

  return useMemo(
    () => ({
      returnReward: openReturnRewardConfirmation,
      returningReward: isReturningReward,
    }),
    [isReturningReward, openReturnRewardConfirmation],
  );
};
